import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ArrowRight, Mail, ChevronLeft, ChevronRight, Tag, Sparkles, Lock } from 'lucide-react';
import { supabase } from '../utils/supabase';
import { isAdminEmail } from '../utils/adminWhitelist';

const LandingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, var(--secondary-color) 0%, #003366 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(255, 143, 0, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(0, 51, 102, 0.3) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const LeftSection = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem 6rem;
  position: relative;
  z-index: 1;

  @media (max-width: 1440px) {
    padding: 4rem 4rem;
  }

  @media (max-width: 1024px) {
    padding: 3rem 2rem;
    text-align: center;
  }

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const RightSection = styled(motion.div)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 3rem 2rem 0;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    padding: 2rem;
    justify-content: center;
    min-height: 400px;
  }

  @media (max-width: 640px) {
    padding: 1.5rem 1rem;
    min-height: 350px;
  }

  @media (max-width: 480px) {
    padding: 1rem;
    min-height: 300px;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 3rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1.5rem;

  img {
    height: 32px;
    width: auto;
    object-fit: contain;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
    transition: all 0.3s ease;
  }

  img:nth-child(2) {
    height: 96px;
  }

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const Headline = styled(motion.h1)`
  font-family: 'Montserrat', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 3.25rem;
  font-weight: 700;
  color: white;
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin: 0 0 2rem 0;
  padding-right: 0.5rem;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.15);

  .gradient-text {
    background: linear-gradient(135deg, #FFD180 0%, var(--primary-color) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: inline-block;
    padding-right: 0.1em;
  }

  @media (max-width: 1440px) {
    font-size: 2.85rem;
  }

  @media (max-width: 1024px) {
    font-size: 2.5rem;
    text-align: center;
  }

  @media (max-width: 640px) {
    font-size: 2rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
    line-height: 1.25;
    margin-bottom: 1.5rem;
  }
`;

const Subheadline = styled(motion.p)`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.65;
  margin: 0 0 2.5rem 0;
  max-width: 540px;
  font-weight: 400;
  letter-spacing: 0.01em;

  .highlight {
    background: linear-gradient(135deg, #FFD180 0%, var(--primary-color) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: inline;
    font-weight: 600;
  }

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }

  @media (max-width: 640px) {
    font-size: 1.1rem;
  }
`;

const EmailForm = styled(motion.form)`
  max-width: 600px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 1rem;

  @media (max-width: 1024px) {
    margin: 0 auto;
    width: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 480px;
  }
`;

const DiscountBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(16, 185, 129, 0.4);
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.25);
  letter-spacing: 0.01em;

  svg {
    width: 16px;
    height: 16px;
    color: white;
  }

  .discount-number {
    font-weight: 700;
    color: white;
    font-size: 0.95rem;
  }
`;

const CountdownTimer = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const TimerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
`;

const TimeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 143, 0, 0.25);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(255, 143, 0, 0.1);

  .time-value {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
    font-variant-numeric: tabular-nums;
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--primary-color);
    line-height: 1;
    margin-bottom: 0.25rem;
  }

  .time-label {
    font-size: 0.6875rem;
    color: rgba(255, 255, 255, 0.5);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  @media (max-width: 640px) {
    padding: 0.625rem 0.375rem;

    .time-value {
      font-size: 1.25rem;
    }

    .time-label {
      font-size: 0.625rem;
    }
  }
`;

const ReassuranceText = styled(motion.p)`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  font-weight: 400;
  letter-spacing: 0.02em;
  grid-column: 1 / -1;

  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const EmailInput = styled.input`
  width: 100%;
  padding: 1rem 1rem 1rem 3rem;
  font-size: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  color: white;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 4px rgba(255, 143, 0, 0.1);
  }

  &.error {
    border-color: #ff6b6b;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 0.875rem 0.875rem 0.875rem 2.75rem;
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 0.875rem 0.875rem 0.875rem 2.5rem;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ErrorMessage = styled(motion.p)`
  color: #ff6b6b;
  font-size: 0.875rem;
  margin-top: -0.5rem;
  margin-left: 0.5rem;
  grid-column: 1 / 2;

  @media (max-width: 768px) {
    grid-column: 1 / -1;
  }
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: 1rem 2rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  box-shadow: 0 8px 24px rgba(255, 143, 0, 0.3);
  transition: all 0.3s ease;
  min-height: 48px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(255, 143, 0, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: 640px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
    min-height: 48px;
  }
`;

const OTPInputWrapper = styled(motion.div)`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OTPInfoText = styled.p`
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  margin: 0;

  .admin-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.75rem;
    background: linear-gradient(135deg, rgba(255, 143, 0, 0.2) 0%, rgba(255, 143, 0, 0.1) 100%);
    border: 1px solid rgba(255, 143, 0, 0.3);
    border-radius: 6px;
    font-weight: 600;
    color: var(--primary-color);

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const OTPInput = styled.input`
  width: 100%;
  padding: 1rem;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: 0.5em;
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  color: white;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 4px rgba(255, 143, 0, 0.1);
  }

  &.error {
    border-color: #ff6b6b;
  }

  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0.875rem;
  }
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TrustIndicators = styled(motion.div)`
  margin-top: 3rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TrustItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;

  svg {
    width: 18px;
    height: 18px;
    color: var(--primary-color);
    flex-shrink: 0;
  }

  @media (max-width: 1024px) {
    justify-content: center;
  }
`;

const HeroImageContainer = styled(motion.div)`
  position: relative;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16 / 9;
  background: linear-gradient(135deg, rgba(0, 51, 102, 0.3) 0%, rgba(255, 143, 0, 0.1) 100%);
  backdrop-filter: blur(10px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  margin-left: -3rem;

  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

const CarouselImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: ${props => props.$objectFit || 'cover'};
  object-position: center;
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
`;

const Dot = styled(motion.button)`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.4)'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: ${props => props.$active ? 'var(--primary-color)' : 'rgba(255, 255, 255, 0.6)'};
    transform: scale(1.2);
  }
`;

const NavButton = styled(motion.button)`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(0, 0, 0, 0.5);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 24px;
    height: 24px;
  }

  &.prev {
    left: 1rem;
  }

  &.next {
    right: 1rem;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &.card-1 {
    top: 10%;
    right: -10%;
    width: 200px;
  }

  &.card-2 {
    bottom: 15%;
    left: -10%;
    width: 220px;
  }

  @media (max-width: 1024px) {
    display: none;
  }
`;

const CardTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--secondary-color);
  margin-bottom: 0.5rem;
`;

const CardValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const LandingPage = ({ onEmailSubmit }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isDiscountActive, setIsDiscountActive] = useState(true);

  // Admin OTP flow state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const carouselImages = [
    {
      src: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Ontwerp%201920x1080%20van%20Claude.webp',
      alt: 'Indus Solution',
      objectFit: 'cover'
    },
    {
      src: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/team.webp',
      alt: 'Indus Team',
      objectFit: 'cover'
    },
    {
      src: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/K-Fair_14-10-2025-13.webp',
      alt: 'K-Fair Exhibition',
      objectFit: 'cover'
    },
    {
      src: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/K-Fair%20Oct%2014%202025.webp',
      alt: 'K-Fair Oct 14 2025',
      objectFit: 'cover'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const targetDate = new Date('2025-10-29T23:59:59');
      const now = new Date();
      const difference = targetDate - now;

      // Check if discount period is still active
      if (difference <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setIsDiscountActive(false);
        return;
      }

      setIsDiscountActive(true);

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    // Check if this is an admin email
    const adminCheck = isAdminEmail(email.trim());
    setIsAdmin(adminCheck);

    if (adminCheck) {
      // ADMIN FLOW: Send OTP code via Supabase Auth
      try {
        const { error } = await supabase.auth.signInWithOtp({
          email: email.trim(),
          options: {
            shouldCreateUser: true,
            channel: 'email'  // Send 6-digit OTP code, not magic link
          }
        });

        if (error) {
          console.error('OTP send error:', error);
          setError('Failed to send verification code. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Success - show OTP input field
        setShowOTPInput(true);
        setIsSubmitting(false);
        console.log('✅ Admin OTP code sent to:', email);

      } catch (err) {
        console.error('Unexpected OTP error:', err);
        setError('An unexpected error occurred. Please try again.');
        setIsSubmitting(false);
      }

    } else {
      // REGULAR USER FLOW: Call Edge Function as before
      try {
        const { data, error } = await supabase.functions.invoke('handle-landing-signup', {
          body: { email: email.trim() }
        });

        if (error) {
          console.error('Edge Function error:', error);
          setError('Failed to process signup. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Check if the response indicates success
        if (!data || !data.success) {
          setError(data?.error || 'Failed to process signup. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Success! Show a brief message before proceeding
        if (data.first_time) {
          // First-time user - discount code was sent
          setError(''); // Clear any errors
          console.log('✅ Discount code sent to:', email);
        } else {
          // Returning user - welcome back
          console.log('👋 Welcome back:', email);
        }

        // Proceed to the app after a brief delay
        setTimeout(() => {
          onEmailSubmit(email);
          setIsSubmitting(false);
        }, 500);

      } catch (err) {
        console.error('Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: otpCode,
        type: 'email'
      });

      if (error) {
        console.error('OTP verification error:', error);
        setError('Invalid or expired code. Please try again.');
        setIsVerifying(false);
        return;
      }

      if (!data?.session) {
        setError('Verification failed. Please try again.');
        setIsVerifying(false);
        return;
      }

      // Success! Store email in localStorage and proceed to app
      console.log('✅ Admin authenticated successfully:', email);
      localStorage.setItem('indus_user_email', email);

      // Proceed to the app
      setTimeout(() => {
        onEmailSubmit(email);
        setIsVerifying(false);
      }, 500);

    } catch (err) {
      console.error('Unexpected verification error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: {
          shouldCreateUser: true,
          channel: 'email'  // Send 6-digit OTP code, not magic link
        }
      });

      if (error) {
        console.error('OTP resend error:', error);
        setError('Failed to resend code. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setIsSubmitting(false);
      console.log('✅ Admin OTP code resent to:', email);

    } catch (err) {
      console.error('Unexpected resend error:', err);
      setError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? carouselImages.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <LandingContainer>
      <LeftSection
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <LogoContainer>
          <img src="https://i.imgur.com/63cuLGv.png" alt="Indus Logo" />
          <img src="https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/K-fair-logo-final.png" alt="K-Fair Logo" />
        </LogoContainer>

        <Headline
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="gradient-text">Configure</span> your bulk handling solution <span className="gradient-text">now</span>.
        </Headline>

        <Subheadline
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          As seen at the <span className="highlight">K-Fair</span> — revolutionize the way you handle big bags with our <span className="highlight">plug and play</span> solutions. <span className="highlight">Fast, smart, and effortless.</span>
        </Subheadline>

        <EmailForm
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {isDiscountActive && (
            <DiscountBadge
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Tag />
              <span><span className="discount-number">10% off</span> • Get code via email</span>
            </DiscountBadge>
          )}

          {isDiscountActive && (
            <CountdownTimer
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <TimerGrid>
                <TimeUnit>
                  <div className="time-value">{timeRemaining.days}</div>
                  <div className="time-label">Days</div>
                </TimeUnit>
                <TimeUnit>
                  <div className="time-value">{String(timeRemaining.hours).padStart(2, '0')}</div>
                  <div className="time-label">Hours</div>
                </TimeUnit>
                <TimeUnit>
                  <div className="time-value">{String(timeRemaining.minutes).padStart(2, '0')}</div>
                  <div className="time-label">Mins</div>
                </TimeUnit>
                <TimeUnit>
                  <div className="time-value">{String(timeRemaining.seconds).padStart(2, '0')}</div>
                  <div className="time-label">Secs</div>
                </TimeUnit>
              </TimerGrid>
            </CountdownTimer>
          )}

          <InputWrapper>
            <InputIcon>
              <Mail />
            </InputIcon>
            <EmailInput
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              className={error ? 'error' : ''}
              disabled={showOTPInput}
            />
          </InputWrapper>

          {error && (
            <ErrorMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              {error}
            </ErrorMessage>
          )}

          {/* Show OTP input for admin users */}
          {showOTPInput && (
            <OTPInputWrapper
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <OTPInfoText>
                <span className="admin-badge">
                  <Lock />
                  Admin Access
                </span>
                <br />
                We've sent a 6-digit code to <strong>{email}</strong>.
                <br />
                Please enter it below to continue.
              </OTPInfoText>

              <OTPInput
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, ''); // Only digits
                  setOtpCode(value);
                  setError('');
                }}
                className={error ? 'error' : ''}
                autoFocus
              />

              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                <span>Didn't receive the code?</span>
                <ResendButton
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isSubmitting}
                >
                  Resend
                </ResendButton>
              </div>
            </OTPInputWrapper>
          )}

          {/* Submit button changes based on OTP input state */}
          {!showOTPInput ? (
            <SubmitButton
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSubmitting ? 'Sending...' : 'Configure Now'}
              <ArrowRight />
            </SubmitButton>
          ) : (
            <SubmitButton
              type="button"
              onClick={handleVerifyOTP}
              disabled={isVerifying || otpCode.length !== 6}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isVerifying ? 'Verifying...' : 'Verify Code'}
              <ArrowRight />
            </SubmitButton>
          )}

          {isDiscountActive && (
            <ReassuranceText
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              Discount code delivered instantly to your inbox
            </ReassuranceText>
          )}
        </EmailForm>

      </LeftSection>

      <RightSection
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <HeroImageContainer
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <AnimatePresence mode="wait">
            <CarouselImage
              key={currentImageIndex}
              src={carouselImages[currentImageIndex].src}
              alt={carouselImages[currentImageIndex].alt}
              $objectFit={carouselImages[currentImageIndex].objectFit}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            />
          </AnimatePresence>

          <NavButton
            className="prev"
            onClick={handlePrevious}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft />
          </NavButton>

          <NavButton
            className="next"
            onClick={handleNext}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronRight />
          </NavButton>

          <CarouselDots>
            {carouselImages.map((_, index) => (
              <Dot
                key={index}
                $active={currentImageIndex === index}
                onClick={() => setCurrentImageIndex(index)}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </CarouselDots>
        </HeroImageContainer>
      </RightSection>
    </LandingContainer>
  );
};

export default LandingPage;
